import { classNames } from 'primereact/utils';
import { useTranslation } from 'react-i18next';

import PenFilledIcon from '../../../../assets/icons/PenFilled';
import PenOutlineIcon from '../../../../assets/icons/PenOutline';
import useSidebarContext from '../../../../hooks/sidebar/useSidebarContext';
import { useFootprintAndProduct } from '../../../../hooks/useFootprintAndProduct';
import CustomIcon from '../../../../shared/components/custom-icon/CustomIcon';
import EmissionValueWithSuffix from '../../../../shared/components/emission-value-with-suffix/EmissionValueWithSuffix';
import { EmissionSymbol } from '../../../../shared/interfaces/IFootprint';
import DetailRow from '../detail-row/DetailRow';
import styles from './EmissionsSection.module.css';

const EmissionsSection = (): JSX.Element => {
  const { t } = useTranslation();
  const { openSidebar } = useSidebarContext();
  const {
    footprintDetails,
    isFootprintLoading: isLoading,
    showEditButton,
  } = useFootprintAndProduct();

  return (
    <div className='bg-white p-4 md:p-5 border-round-2xl flex flex-column w-full'>
      {/* Title */}
      <div className='flex flex-row gap-3 align-items-center pb-3'>
        <div className='gap-2 flex'>
          <div className='bg-primary p-1' />
          <h5 className='m-0 p-0 font-medium'>
            {t('footprintDetailsPage.sections.emissionsSection.title')}
          </h5>
        </div>
        {showEditButton && (
          <CustomIcon
            icon={PenOutlineIcon}
            hoverIcon={PenFilledIcon}
            showTextOnHover
            hoverText={t('global.edit')}
            hoverTextColor='primary'
            className='cursor-pointer flex justify-content-center align-items-center'
            onClick={() => {
              openSidebar('edit-emissions-details-sidebar', {
                position: 'right',
                footprintDetails: footprintDetails,
              });
            }}
          />
        )}
      </div>

      {/* Fossil Emissions */}
      <h6 className='m-0 p-0 pb-2 font-semibold text-gray-700'>
        {t('footprintDetailsPage.sections.emissionsSection.fossilEmissionsTitle')}
      </h6>

      {/* Fossil Ghg Emissions */}
      <DetailRow
        label={t('footprintDetailsPage.sections.emissionsSection.fossilGhgEmissions')}
        isLoading={isLoading}
      >
        <EmissionValueWithSuffix
          emissionValue={footprintDetails?.pcf?.fossil_ghg_emissions}
          emissionSymbol={EmissionSymbol.KG_CO2E}
          declaredUnit={footprintDetails?.pcf?.declared_unit}
          abbreviateUnit
          unitaryProductAmount={footprintDetails?.pcf?.unitary_product_amount}
          suffixTextClassName='text-black-alpha-60'
          numericalValueTextClassName='font-bold text-black-alpha-60'
        />
      </DetailRow>

      {/* Carbon Content */}
      <h6 className='m-0 p-0 pt-4 pb-2 font-semibold text-gray-700'>
        {t('footprintDetailsPage.sections.emissionsSection.carbonContentTitle')}
      </h6>

      {/* Fossil Carbon Content */}
      <DetailRow
        label={t('footprintDetailsPage.sections.emissionsSection.fossilCarbonContent')}
        isLoading={isLoading}
      >
        <EmissionValueWithSuffix
          emissionValue={footprintDetails?.pcf?.fossil_carbon_content}
          emissionSymbol={EmissionSymbol.KG_C}
          declaredUnit={footprintDetails?.pcf?.declared_unit}
          abbreviateUnit
          unitaryProductAmount={footprintDetails?.pcf?.unitary_product_amount}
          suffixTextClassName='text-black-alpha-60'
          numericalValueTextClassName='font-bold text-black-alpha-60'
        />
      </DetailRow>

      {/* Biogenic Carbon Content */}
      <DetailRow
        label={t('footprintDetailsPage.sections.emissionsSection.biogenicCarbonContent')}
        isLoading={isLoading}
      >
        <EmissionValueWithSuffix
          emissionValue={footprintDetails?.pcf?.carbon_content_biogenic}
          emissionSymbol={EmissionSymbol.KG_C}
          declaredUnit={footprintDetails?.pcf?.declared_unit}
          abbreviateUnit
          unitaryProductAmount={footprintDetails?.pcf?.unitary_product_amount}
          suffixTextClassName='text-black-alpha-60'
          numericalValueTextClassName='font-bold text-black-alpha-60'
        />
      </DetailRow>

      {/* Total */}
      <h6 className='m-0 p-0 pt-4 pb-2 font-semibold text-gray-700'>
        {t('footprintDetailsPage.sections.emissionsSection.totalTitle')}
      </h6>

      <div className='flex flex-column gap-2'>
        {/* Pcf excl Biogenic Emissions and Removals */}
        <div className={classNames('px-4 border-round-2xl flex flex-column', styles['card'])}>
          <DetailRow
            label={t(
              'footprintDetailsPage.sections.emissionsSection.pcfExclBiogenicEmissionsAndRemovals',
            )}
            labelClassName='font-medium text-primary'
            isLoading={isLoading}
            showDivider={false}
          >
            <EmissionValueWithSuffix
              emissionValue={footprintDetails?.pcf?.pcf_excluding_biogenic}
              emissionSymbol={EmissionSymbol.KG_CO2E}
              declaredUnit={footprintDetails?.pcf?.declared_unit}
              abbreviateUnit
              unitaryProductAmount={footprintDetails?.pcf?.unitary_product_amount}
              suffixTextClassName='text-primary'
              numericalValueTextClassName='font-bold text-primary'
            />
          </DetailRow>
        </div>

        {/* Pcf incl Biogenic Emissions and Removals */}
        <div className={classNames('px-4 border-round-2xl flex flex-column', styles['card'])}>
          <DetailRow
            label={t(
              'footprintDetailsPage.sections.emissionsSection.pcfInclBiogenicEmissionsAndRemovals',
            )}
            isLoading={isLoading}
            showDivider={false}
          >
            <EmissionValueWithSuffix
              emissionValue={footprintDetails?.pcf?.pcf_including_biogenic}
              emissionSymbol={EmissionSymbol.KG_CO2E}
              declaredUnit={footprintDetails?.pcf?.declared_unit}
              abbreviateUnit
              unitaryProductAmount={footprintDetails?.pcf?.unitary_product_amount}
              suffixTextClassName='text-black-alpha-60'
              numericalValueTextClassName='font-bold text-black-alpha-60'
            />
          </DetailRow>
        </div>
      </div>
    </div>
  );
};

export default EmissionsSection;
